import { retryAfterUnauthorized } from "./session";
import { API_BASE_URL } from "../config";

/**
 * Workflow Template and Instance API
 * Handles workflow template management and workflow instance creation
 */

const getAuthHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  // Add auth token if available (same pattern as api.js)
  const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
  if (user.token) {
    headers["Authorization"] = `Bearer ${user.token}`;
  }

  return headers;
};

// FastAPI's error `detail` is a string for HTTPException but an array of
// {loc,msg,type} objects for 422 validation errors — stringifying that array
// yields "[object Object]", so flatten it to the joined msg fields.
const detailToMessage = (detail, fallback) => {
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ") || fallback;
  }
  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }
  return fallback;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "An error occurred" }));

    // Handle 401 Unauthorized - Token expired or invalid (same as api.js)
    if (response.status === 401) {
      console.warn("Authentication failed - token expired or invalid. Logging out...");

      // the access token may simply have expired: renew it, then the caller retries

      await retryAfterUnauthorized(() => undefined);

      throw new Error(detailToMessage(error.detail, "Session expired. Please login again."));
    }

    throw new Error(detailToMessage(error.detail, `HTTP error! status: ${response.status}`));
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return null;
  }

  return response.json();
};

// ============================================================================
// WORKFLOW TEMPLATES API
// ============================================================================

export const workflowTemplateApi = {
  /**
   * Get all workflow templates (global + org templates)
   * @param {Object} filters - Optional filters { domain, agency }
   * @returns {Promise<Array>} List of templates
   */
  getTemplates: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.domain) {
      params.append("domain", filters.domain);
    }
    if (filters.agency) {
      params.append("agency", filters.agency);
    }

    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/workflow-templates${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },

  /**
   * Get a specific workflow template by ID
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Template details
   */
  getTemplate: async (templateId) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-templates/${templateId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },

  /**
   * Create a new workflow template
   * @param {Object} templateData - { name, description, agency, domain, stages }
   * @returns {Promise<Object>} Created template
   */
  createTemplate: async (templateData) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-templates`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(templateData),
    });

    return handleResponse(response);
  },

  /**
   * Update a workflow template
   * - If global template & user not from CertBuddyAI → creates fork
   * - If org template → updates directly
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Fields to update
   * @returns {Promise<Object>} Updated template (or forked copy)
   */
  updateTemplate: async (templateId, templateData) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-templates/${templateId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(templateData),
    });

    return handleResponse(response);
  },

  /**
   * Delete a workflow template
   * Only allowed for org templates (not global templates)
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Success message
   */
  deleteTemplate: async (templateId) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-templates/${templateId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },

  /**
   * Get unique domains and agencies from all visible templates
   * @returns {Promise<Object>} { domains: string[], agencies: string[], domain_agency_map: Object }
   */
  getDomainsAndAgencies: async () => {
    const response = await fetch(
      `${API_BASE_URL}/api/workflow-templates/metadata/domains-agencies`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );

    return handleResponse(response);
  },
};

// ============================================================================
// WORKFLOW INSTANCES API
// ============================================================================

export const workflowInstanceApi = {
  /**
   * Create a workflow instance from a template
   * @param {Object} instanceData - {
   *   template_id: string,
   *   project_id: string,
   *   name: string,
   *   customized_stages?: Array<{stage_number: number, prompt?: string}>
   * }
   * @returns {Promise<Object>} Created instance
   */
  createInstance: async (instanceData) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-instances`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(instanceData),
    });

    return handleResponse(response);
  },

  /**
   * Get all workflow instances for a project
   * @param {string} projectId - Project ID (required)
   * @param {Object} filters - Optional filters { agency, status }
   * @returns {Promise<Array>} List of instances
   */
  getInstances: async (projectId, filters = {}) => {
    const params = new URLSearchParams({ project_id: projectId });
    if (filters.agency) {
      params.append("agency", filters.agency);
    }
    if (filters.status) {
      params.append("status", filters.status);
    }

    const response = await fetch(`${API_BASE_URL}/api/workflow-instances?${params.toString()}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },

  /**
   * Get a specific workflow instance by ID
   * @param {string} instanceId - Instance ID
   * @returns {Promise<Object>} Instance details
   */
  getInstance: async (instanceId) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-instances/${instanceId}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },

  /**
   * Update a workflow instance
   * @param {string} instanceId - Instance ID
   * @param {Object} updateData - Fields to update { name, stages, status, current_stage, stage_data }
   * @returns {Promise<Object>} Updated instance
   */
  updateInstance: async (instanceId, updateData) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-instances/${instanceId}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(updateData),
    });

    return handleResponse(response);
  },

  /**
   * Update a specific stage prompt in a workflow instance
   * Allows per-instance customization without affecting template
   * @param {string} instanceId - Instance ID
   * @param {number} stageNumber - Stage number
   * @param {string} newPrompt - New prompt text
   * @returns {Promise<Object>} Updated instance
   */
  updateStagePrompt: async (instanceId, stageNumber, newPrompt) => {
    const response = await fetch(
      `${API_BASE_URL}/api/workflow-instances/${instanceId}/stage-prompt`,
      {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          stage_number: stageNumber,
          new_prompt: newPrompt,
        }),
      }
    );

    return handleResponse(response);
  },

  /**
   * Delete a workflow instance
   * @param {string} instanceId - Instance ID
   * @returns {Promise<Object>} Success message
   */
  deleteInstance: async (instanceId) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-instances/${instanceId}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    return handleResponse(response);
  },

  /**
   * Get workflow names for multiple workflow IDs at once
   * @param {Array<string>} workflowIds - Array of workflow instance IDs
   * @returns {Promise<Object>} Dictionary mapping workflow_id -> workflow_name
   */
  getBatchNames: async (workflowIds) => {
    if (!workflowIds || workflowIds.length === 0) {
      return {};
    }

    const response = await fetch(`${API_BASE_URL}/api/workflow-instances/batch-names`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(workflowIds),
    });

    return handleResponse(response);
  },
};

// ============================================================================
// WORKFLOW STAGE CONTENT EDIT & REGENERATION API
// ============================================================================

/**
 * Submit a select-to-edit instruction for a specific stage field.
 * @param {string} instanceId
 * @param {number} stageNumber
 * @param {Object} payload - { field, selected_text, instruction, source?, source_id? }
 * @returns {Promise<Object>} Updated stage content
 */
export async function editStageContent(instanceId, stageNumber, payload) {
  const response = await fetch(
    `${API_BASE_URL}/api/workflow-instances/${instanceId}/stages/${stageNumber}/content/edit`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  return handleResponse(response);
}

const stagePromptUrl = (instanceId, stageNumber, suffix = "") =>
  `${API_BASE_URL}/api/workflow-instances/${instanceId}/stages/${stageNumber}/prompt${suffix}`;

/**
 * Preview an AI-improved stage prompt. Stores nothing — call it as often as you like.
 * @param {Object} payload - { prompt, engine? } engine: "agents" | "research"
 * @returns {Promise<Object>} { original, improved, engine, notes }
 */
export async function improveStagePrompt(instanceId, stageNumber, payload) {
  const response = await fetch(stagePromptUrl(instanceId, stageNumber, "/improve"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

/** The stage's prompt version history, oldest first. Empty until first use. */
export async function getStagePromptVersions(instanceId, stageNumber) {
  const response = await fetch(stagePromptUrl(instanceId, stageNumber, "/versions"), {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

/**
 * Save a prompt as a new version AND make it the one generation runs.
 * @param {Object} payload - { prompt, original?, note? }
 */
export async function saveStagePromptVersion(instanceId, stageNumber, payload) {
  const response = await fetch(stagePromptUrl(instanceId, stageNumber, "/versions"), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

/** Re-apply an earlier version as a new version. */
export async function restoreStagePromptVersion(instanceId, stageNumber, version) {
  const response = await fetch(
    stagePromptUrl(instanceId, stageNumber, `/versions/${version}/restore`),
    { method: "POST", headers: getAuthHeaders() }
  );
  return handleResponse(response);
}

/**
 * Replace a stage report field wholesale with user-edited text (direct WYSIWYG save).
 * @param {string} instanceId
 * @param {number} stageNumber
 * @param {Object} payload - { field, content }
 * @returns {Promise<Object>} { field, new_content }
 */
export async function setStageContent(instanceId, stageNumber, payload) {
  const response = await fetch(
    `${API_BASE_URL}/api/workflow-instances/${instanceId}/stages/${stageNumber}/content/set`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  return handleResponse(response);
}

/**
 * Propose a full-stage regeneration (returns a proposal, not yet applied).
 * @param {string} instanceId
 * @param {number} stageNumber
 * @param {Object} payload - { feedback_ids?, comment_ids?, extra_instruction? }
 * @returns {Promise<Object>} Proposal with proposed_fields and change_log
 */
export async function regenerateStage(instanceId, stageNumber, payload) {
  const response = await fetch(
    `${API_BASE_URL}/api/workflow-instances/${instanceId}/stages/${stageNumber}/regenerate`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  return handleResponse(response);
}

/**
 * Audit a finished report claim by claim against the corpus.
 *
 * On demand only — one retrieval plus one judgement per claim is real cost. Each finding
 * carries the corpus chunks that justify it and, where the corpus contradicts the claim,
 * a replacement that applyReviewFinding can apply.
 *
 * @param {string} instanceId
 * @param {number} stageNumber
 * @param {string} field - one of executive_summary | certgpt_answer | full_report
 * @returns {Promise<{findings: Object[], count: number}>}
 */
export async function reviewStageReport(instanceId, stageNumber, field = "full_report") {
  const response = await fetch(
    `${API_BASE_URL}/api/workflow-instances/${instanceId}/stages/${stageNumber}/review?field=${encodeURIComponent(field)}`,
    { method: "POST", headers: getAuthHeaders() }
  );
  return handleResponse(response);
}

/**
 * Apply one review finding's correction. Routes through the same exact-match replacement
 * and edit-history path as every other edit, so it is versioned and revertible.
 *
 * @param {string} instanceId
 * @param {number} stageNumber
 * @param {{field: string, old_string: string, new_string: string, reason?: string}} finding
 */
export async function applyReviewFinding(instanceId, stageNumber, finding) {
  const response = await fetch(
    `${API_BASE_URL}/api/workflow-instances/${instanceId}/stages/${stageNumber}/review/apply`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(finding),
    }
  );
  return handleResponse(response);
}

/**
 * Apply the last accepted stage regeneration proposal.
 * @param {string} instanceId
 * @param {number} stageNumber
 * @returns {Promise<Object>} Updated stage content
 */
export async function applyStageRegeneration(instanceId, stageNumber) {
  const response = await fetch(
    `${API_BASE_URL}/api/workflow-instances/${instanceId}/stages/${stageNumber}/regenerate/apply`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );
  return handleResponse(response);
}

// ============================================================================
// WORKFLOW DRAFT API — build a workflow from an uploaded document
// ============================================================================

export const workflowDraftApi = {
  /**
   * Upload a document and find out what it covers.
   * @param {File} file - PDF, CSV or XLSX
   * @returns {Promise<Object>} { doc_kind, summary, subjects, suggested_agency,
   *                              suggested_domain, doc_token, source }
   */
  inspectDocument: async (file) => {
    const user = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "{}");
    const form = new FormData();
    form.append("file", file);

    const response = await fetch(`${API_BASE_URL}/api/workflow-drafts/inspect`, {
      method: "POST",
      // no Content-Type: the browser must set the multipart boundary itself
      headers: user.token ? { Authorization: `Bearer ${user.token}` } : {},
      body: form,
    });

    return handleResponse(response);
  },

  /**
   * The original uploaded file (or its PDF conversion, for DOCX), for the real
   * preview. 404s for artifacts uploaded before file persistence existed.
   * @param {string} docToken
   * @returns {Promise<{blob: Blob, mime: string}>}
   */
  getOriginalFile: async (docToken) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-drafts/${docToken}/file`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(
        response.status === 404
          ? "No preview is available for this document — try re-uploading it."
          : "Failed to load the document preview."
      );
    }
    const blob = await response.blob();
    return { blob, mime: response.headers.get("Content-Type") || blob.type };
  },

  /**
   * The items a document could be split into, for a grouping mode.
   * Pure parsing on the server — no LLM call, so switching modes is instant.
   * @param {Object} payload - { doc_token, mode, key_column }
   * @returns {Promise<Object>} { candidates: [{label, kind, evidence}], max_selected }
   */
  getCandidates: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-drafts/candidates`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return handleResponse(response);
  },

  /**
   * Which rows of a sheet apply to one component. An LLM judgement per row, each
   * with a reason so the user can correct it — this proposes a selection, it does
   * not commit one.
   * @param {Object} payload - { doc_token, component, table }
   * @returns {Promise<Object>} { table, component, applicable_count,
   *                              items: [{row, applicable, confidence, reason}] }
   */
  getApplicableItems: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-drafts/applicable-items`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return handleResponse(response);
  },

  /**
   * One prompt from pasted text — no upload, no artifact, nothing persisted.
   * @param {Object} payload - { text, intent, title }
   * @returns {Promise<Object>} { prompt: {name, prompt} }
   */
  promptFromText: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-drafts/prompt-from-text`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return handleResponse(response);
  },

  /**
   * Generate an editable draft for the chosen scope. Persists nothing.
   * Passing selected_labels returns { prompts } instead of { draft } — one
   * scoped prompt per selected item. In "selection" mode, components (what the
   * workflow is FOR) and item_selections/cell_selections/text_selections (what it
   * COVERS) combine into one scoped { draft }.
   * @param {Object} payload - { doc_token, subject_labels, intent, group_mode,
   *                             selected_labels, cell_selections, text_selections,
   *                             components, item_selections }
   * @returns {Promise<Object>} { draft } or { prompts }
   */
  draftWorkflow: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-drafts/draft`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return handleResponse(response);
  },

  /**
   * Persist the reviewed draft as a template and instantiate it on the project.
   * @param {Object} payload - { name, description, agency, domain, project_id, stages }
   * @returns {Promise<Object>} { template, instance }
   */
  createWorkflow: async (payload) => {
    const response = await fetch(`${API_BASE_URL}/api/workflow-drafts/create`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return handleResponse(response);
  },
};

// Default export for convenience
const workflowApi = {
  templates: workflowTemplateApi,
  instances: workflowInstanceApi,
  drafts: workflowDraftApi,
};

export default workflowApi;
